'use client';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

/* ─── DATA ──────────────────────────────────────────────── */

const launchSteps = [
  {
    num: '01',
    icon: 'BuildingStorefrontIcon',
    title: 'Set Up Your Store',
    desc: 'Add your logo, store name, product catalog, pricing, and delivery zones. No technical knowledge needed.',
  },
  {
    num: '02',
    icon: 'PaintBrushIcon',
    title: 'Brand Your App',
    desc: 'Choose your app theme, colors, and banner. Your store goes live under your own name on iOS and Android.',
  },
  {
    num: '03',
    icon: 'ShareIcon',
    title: 'Share & Start Selling',
    desc: 'Share your app download link or WhatsApp store number. Orders start flowing in from day one.',
  },
];

const coreFeatures = [
  {
    num: '01',
    icon: 'DevicePhoneMobileIcon',
    label: 'Branded Store App for iOS & Android',
    desc: 'Your store launches under your own name, logo, and colors — not a marketplace. Customers download YOUR app. Full brand ownership, no platform fees, no competitors shown alongside your products.',
    pills: ['Custom branding', 'iOS & Android', 'Web store link', 'QR code access'],
    size: 'full',
  },
  {
    num: '02',
    icon: 'ShoppingCartIcon',
    label: 'Product Catalog & Order Management',
    desc: 'Build your catalog with categories, variants, and product photos. All incoming orders appear in a live dashboard — accept, prepare, and dispatch with one tap.',
    pills: ['Categories & variants', 'Live order dashboard', 'Auto-accept option'],
    size: 'third',
  },
  {
    num: '03',
    icon: 'CreditCardIcon',
    label: 'Integrated Payments & Invoicing',
    desc: 'Accept UPI, cards, wallets, and cash on delivery. Every order auto-generates a branded, GST-ready digital invoice shared instantly with the customer.',
    pills: ['UPI & cards', 'COD support', 'Auto-invoicing', 'GST-ready'],
    size: 'third',
  },
  {
    num: '04',
    icon: 'TruckIcon',
    label: 'Own Delivery Fleet Management',
    desc: 'Add your delivery riders to the platform. Orders auto-assign or dispatch manually. Riders navigate with the Rider App — customers track live from your branded app.',
    pills: ['Rider app', 'Auto-assign', 'Live tracking'],
    size: 'third',
  },
  {
    num: '05',
    icon: 'ChatBubbleLeftRightIcon',
    label: 'WhatsApp Order Notifications',
    desc: 'Every order stage — placed, confirmed, out for delivery, and delivered — triggers an automatic WhatsApp notification to the customer. Works without WhatsApp Business API setup.',
    pills: ['All order stages', 'Customer & store alerts', 'Auto-triggered', 'No extra setup'],
    size: 'wide',
  },
  {
    num: '06',
    icon: 'ChartPieIcon',
    label: 'Store Performance Dashboard',
    desc: 'Daily sales, top products, peak order hours, customer retention, and revenue trends — all in one visual dashboard built for store owners, not data analysts.',
    pills: ['Daily revenue', 'Top products', 'Customer insights'],
    size: 'third',
  },
];

const orderChannels = [
  {
    icon: 'DevicePhoneMobileIcon',
    label: 'Your Branded App',
    sub: 'iOS & Android',
    desc: 'Customers browse, add to cart, and order directly from your store\'s own app. Full branded experience.',
    color: '#7C3AED',
    bg: 'rgba(124,58,237,0.08)',
  },
  {
    icon: 'ChatBubbleLeftRightIcon',
    label: 'WhatsApp Ordering',
    sub: 'No app download needed',
    desc: 'Customers message your store on WhatsApp. Chatbot takes the order, confirms, and sends tracking updates.',
    color: '#16A34A',
    bg: 'rgba(22,163,74,0.08)',
  },
  {
    icon: 'GlobeAltIcon',
    label: 'Web Store Link',
    sub: 'Works on any browser',
    desc: 'A shareable web link lets customers order from any device — ideal for Instagram bio, Google, or QR codes.',
    color: '#0459F7',
    bg: 'rgba(4,89,247,0.08)',
  },
];

const customerAppFeatures = [
  'Browse Catalog by Category',
  'Smart Product Search & Filters',
  'Product Variants & Add-ons',
  'Add to Cart & Wishlist',
  'Multiple Delivery Addresses',
  'Live Order Tracking on Map',
  'Reorder Previous Orders',
  'Loyalty Points & Referral',
  'Digital Receipts & Order History',
  'Push & WhatsApp Notifications',
  'Multiple Payment Options',
  'Rate & Review Products',
];

const storeFeatures = [
  'Live Order Dashboard',
  'Product Catalog & Category Builder',
  'Inventory & Stock Control',
  'Pricing, Variants & Discounts',
  'Banner & Promotion Setup',
  'Customer Management & CRM',
  'Rider & Delivery Management',
  'Delivery Zone Configuration',
  'WhatsApp Notification Config',
  'Staff Role & Access Control',
  'GST & Tax Configuration',
  'Sales Reports & Analytics',
];

const riderFeatures = [
  'Accept / Reject Deliveries',
  'Optimised Navigation',
  'Live Order List by Priority',
  'Proof of Delivery (OTP / Photo)',
  'Customer Call Masking',
  'Daily Earnings Dashboard',
  'Performance Score',
  'Trip History',
];

const additionalFeatures = [
  { icon: 'CalendarDaysIcon', label: 'Scheduled & Advance Orders', sub: 'Customers pre-book for a specific time' },
  { icon: 'QrCodeIcon', label: 'QR Code Store Access', sub: 'Scan to open web store or app instantly' },
  { icon: 'ShieldCheckIcon', label: 'Returns & Refund Management', sub: 'Accept returns and issue store credits' },
  { icon: 'DocumentTextIcon', label: 'Bulk Product Import', sub: 'Upload full catalog via CSV in minutes' },
  { icon: 'BellAlertIcon', label: 'Low-stock Alerts', sub: 'Get notified before products run out' },
  { icon: 'AdjustmentsHorizontalIcon', label: 'Minimum Order & Delivery Fee', sub: 'Set rules per zone or order value' },
];

const dashboardMetrics = [
  { icon: 'ChartBarIcon', label: 'Daily Revenue', sub: 'Real-time sales with hourly trend' },
  { icon: 'ShoppingCartIcon', label: 'Order Volume', sub: 'Total orders by day, week, month' },
  { icon: 'StarIcon', label: 'Top Products', sub: 'Best-sellers by revenue & units' },
  { icon: 'ArrowPathIcon', label: 'Repeat Customer Rate', sub: 'Measure customer loyalty over time' },
  { icon: 'MapPinIcon', label: 'Zone-wise Delivery Stats', sub: 'Delivery time and cost per area' },
  { icon: 'BoltIcon', label: 'Peak Hour Analysis', sub: 'Identify your busiest order windows' },
];

const stats = [
  { value: '3 Channels', label: 'App · WhatsApp · Web', icon: 'DevicePhoneMobileIcon' },
  { value: 'Your Brand', label: 'Own App & Store Identity', icon: 'BuildingStorefrontIcon' },
  { value: 'Own Fleet', label: 'Manage Your Riders', icon: 'TruckIcon' },
  { value: '21 Days', label: 'Go-Live Timeline', icon: 'BoltIcon' },
];

/* ─── COMPONENT ──────────────────────────────────────────── */

export default function SingleStoreContent() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (el) {
      const t = setTimeout(() => {
        el.querySelectorAll('.animate-on-scroll').forEach((item) => item.classList.add('is-visible'));
      }, 30);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="pt-20">

      {/* ── HERO (violet gradient + 3-step launch panel) ── */}
      <div
        ref={heroRef}
        className="relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1E1B4B 0%, #4C1D95 45%, #7C3AED 100%)' }}
      >
        <div className="absolute inset-0 grid-lines opacity-15 pointer-events-none" />
        <div className="orb absolute w-[600px] h-[600px] rounded-full opacity-15 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, transparent 65%)', top: '-200px', right: '-150px' }} />
        <div className="orb-2 absolute w-[400px] h-[400px] rounded-full opacity-10 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(167,139,250,0.6) 0%, transparent 70%)', bottom: '-80px', left: '-80px' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Left */}
            <div>
              <Link href="/products" className="animate-on-scroll stagger-1 inline-flex items-center gap-2 text-white/50 text-sm font-medium mb-7 hover:text-white/80 transition-colors">
                <Icon name="ArrowLeftIcon" size={14} />
                Back to Products
              </Link>

              <span className="animate-on-scroll stagger-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#C4B5FD]/40 bg-[#C4B5FD]/10 text-[11px] font-semibold tracking-widest text-[#C4B5FD] uppercase mb-5">
                <Icon name="BuildingStorefrontIcon" size={11} />
                Single Store
              </span>

              <h1 className="animate-on-scroll stagger-2 font-display text-5xl md:text-6xl font-semibold text-white leading-tight tracking-tight mb-5 mt-2">
                Your store.
                <br />
                <span className="italic font-light" style={{ color: '#C4B5FD' }}>Your brand.</span>
                <br />
                Your rules.
              </h1>

              <p className="animate-on-scroll stagger-3 text-white/60 text-lg leading-relaxed mb-9 max-w-lg">
                Go from a physical store to a fully digital business in 21 days — with your own branded app, online catalog, integrated payments, delivery fleet, and WhatsApp ordering.
              </p>

              <div className="animate-on-scroll stagger-4 flex flex-wrap gap-4 mb-8">
                <Link
                  href="/demo-booking"
                  className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-sm font-semibold text-[#1E1B4B] hover:opacity-90 shadow-lg transition-all hover:-translate-y-0.5"
                  style={{ background: 'linear-gradient(135deg, #C4B5FD, #A78BFA)' }}
                >
                  Book a Free Demo
                  <Icon name="ArrowRightIcon" size={16} />
                </Link>
                <Link href="/contact" className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-sm font-medium text-white/75 border border-white/20 hover:border-white/45 hover:text-white transition-all">
                  <Icon name="PhoneIcon" size={15} />
                  +91 9600830488
                </Link>
              </div>

              <div className="animate-on-scroll stagger-5 flex flex-wrap gap-x-6 gap-y-2">
                {['21-day go-live', 'No marketplace fees', 'Own branded app', 'WhatsApp ordering'].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5 text-xs text-white/45 font-medium">
                    <Icon name="CheckCircleIcon" size={13} className="text-[#A78BFA]" />
                    {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Right — 3-step launch panel */}
            <div className="animate-on-scroll stagger-3">
              <div
                className="rounded-2xl overflow-hidden border border-white/10"
                style={{ background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(16px)' }}
              >
                <div className="px-6 py-4 border-b border-white/[0.08]">
                  <p className="text-[11px] font-semibold text-white/35 uppercase tracking-widest">Launch Your Store in 3 Steps</p>
                </div>
                <div className="p-5 flex flex-col gap-3">
                  {launchSteps.map((step, i) => (
                    <div
                      key={step.num}
                      className="flex gap-4 p-4 rounded-xl border border-white/[0.08] hover:bg-white/[0.04] transition-colors"
                    >
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, #7C3AED, #6D28D9)' }}
                      >
                        <Icon name={step.icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] font-bold text-[#C4B5FD]/60 uppercase tracking-wider">Step {step.num}</span>
                          <span className="font-semibold text-white text-sm">{step.title}</span>
                        </div>
                        <p className="text-xs text-white/45 leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-5 pb-5">
                  <div className="rounded-xl p-4 flex items-center gap-3" style={{ background: 'rgba(124,58,237,0.25)', border: '1px solid rgba(167,139,250,0.25)' }}>
                    <Icon name="BoltIcon" size={16} className="text-[#C4B5FD] flex-shrink-0" />
                    <span className="text-xs text-white/65 font-medium">No technical team needed. Setup is fully guided and support is available throughout.</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── METRICS STRIP ── */}
      <div style={{ background: 'linear-gradient(135deg, #1E1B4B 0%, #4C1D95 60%, #6D28D9 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {stats.map((s, i) => (
              <div key={s.label} className={`animate-on-scroll stagger-${i + 1} flex items-center gap-4 px-6 py-7`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-white/15">
                  <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-[#C4B5FD]" />
                </div>
                <div>
                  <div className="font-display text-2xl font-semibold text-white leading-none mb-0.5">{s.value}</div>
                  <div className="text-xs text-white/55 font-medium">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CORE 6 FEATURES — BENTO GRID ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(124,58,237,0.3)] bg-[rgba(124,58,237,0.07)] text-[11px] font-semibold tracking-widest text-[#7C3AED] uppercase mb-4">
              Key Features
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Six features that take your{' '}
              <span className="italic font-light text-[#7C3AED]">store fully digital.</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-2xl mx-auto">Everything an independent store owner needs — catalog, orders, payments, delivery, and analytics — in one platform.</p>
          </div>

          {/* Bento grid */}
          <div className="grid md:grid-cols-3 gap-5">

            {/* Feature 01 — full width, horizontal */}
            <div
              className="animate-on-scroll stagger-2 md:col-span-3 group flex flex-col sm:flex-row gap-6 p-8 rounded-2xl border border-[rgba(124,58,237,0.15)] hover:border-[rgba(124,58,237,0.35)] hover:shadow-lg transition-all duration-300"
              style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.06), rgba(109,40,217,0.02))' }}
            >
              <div className="flex items-start gap-5 sm:w-[45%]">
                <span className="font-mono-custom text-[11px] font-bold w-8 h-8 rounded-full flex items-center justify-center text-white flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #7C3AED, #6D28D9)' }}>01</span>
                <div>
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                    style={{ background: 'rgba(124,58,237,0.12)' }}>
                    <Icon name="DevicePhoneMobileIcon" size={26} className="text-[#7C3AED]" />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-[#0F172A] mb-3">{coreFeatures[0].label}</h3>
                  <div className="flex flex-wrap gap-2">
                    {coreFeatures[0].pills.map((p) => (
                      <span key={p} className="text-[11px] font-semibold px-2.5 py-1 rounded-full text-[#7C3AED]" style={{ background: 'rgba(124,58,237,0.08)' }}>{p}</span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="sm:w-[55%] flex items-center">
                <p className="text-[#475569] text-base leading-relaxed">{coreFeatures[0].desc}</p>
              </div>
            </div>

            {/* Features 02, 03, 04 — thirds */}
            {coreFeatures.slice(1, 4).map((f, i) => (
              <div
                key={f.num}
                className={`animate-on-scroll stagger-${i + 3} group p-7 rounded-2xl border border-[rgba(124,58,237,0.1)] hover:border-[rgba(124,58,237,0.3)] hover:shadow-md transition-all duration-300 ${i === 1 ? 'bg-[#FAF5FF]' : 'bg-white'}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-mono-custom text-[10px] font-bold w-7 h-7 rounded-full flex items-center justify-center text-white"
                    style={{ background: 'linear-gradient(135deg, #7C3AED, #6D28D9)' }}>{f.num}</span>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform"
                    style={{ background: 'rgba(124,58,237,0.1)' }}>
                    <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={19} className="text-[#7C3AED]" />
                  </div>
                </div>
                <h3 className="font-display text-base font-semibold text-[#0F172A] mb-2">{f.label}</h3>
                <p className="text-sm text-[#475569] leading-relaxed mb-3">{f.desc}</p>
                <div className="flex flex-wrap gap-1.5">
                  {f.pills.map((p) => (
                    <span key={p} className="text-[10px] font-semibold px-2 py-0.5 rounded-full text-[#7C3AED]" style={{ background: 'rgba(124,58,237,0.07)' }}>{p}</span>
                  ))}
                </div>
              </div>
            ))}

            {/* Feature 05 — wide (2/3) */}
            <div
              className="animate-on-scroll stagger-3 md:col-span-2 group flex flex-col sm:flex-row gap-6 p-7 rounded-2xl border border-[rgba(22,163,74,0.2)] hover:border-[rgba(22,163,74,0.4)] hover:shadow-md transition-all duration-300 bg-[#F0FDF4]"
            >
              <div className="flex items-start gap-4 sm:w-[40%]">
                <span className="font-mono-custom text-[10px] font-bold w-7 h-7 rounded-full flex items-center justify-center text-white flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #16A34A, #15803D)' }}>05</span>
                <div>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform bg-[rgba(22,163,74,0.12)]">
                    <Icon name="ChatBubbleLeftRightIcon" size={22} className="text-[#16A34A]" />
                  </div>
                  <h3 className="font-display text-base font-semibold text-[#0F172A] mb-2">{coreFeatures[4].label}</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {coreFeatures[4].pills.map((p) => (
                      <span key={p} className="text-[10px] font-semibold px-2 py-0.5 rounded-full text-[#16A34A]" style={{ background: 'rgba(22,163,74,0.08)' }}>{p}</span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="sm:w-[60%] flex items-center">
                <p className="text-sm text-[#475569] leading-relaxed">{coreFeatures[4].desc}</p>
              </div>
            </div>

            {/* Feature 06 — single (1/3) */}
            <div
              className="animate-on-scroll stagger-4 group p-7 rounded-2xl border border-[rgba(124,58,237,0.12)] hover:border-[rgba(124,58,237,0.3)] hover:shadow-md transition-all duration-300 bg-[#FAF5FF]"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="font-mono-custom text-[10px] font-bold w-7 h-7 rounded-full flex items-center justify-center text-white"
                  style={{ background: 'linear-gradient(135deg, #7C3AED, #6D28D9)' }}>06</span>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform"
                  style={{ background: 'rgba(124,58,237,0.1)' }}>
                  <Icon name="ChartPieIcon" size={19} className="text-[#7C3AED]" />
                </div>
              </div>
              <h3 className="font-display text-base font-semibold text-[#0F172A] mb-2">{coreFeatures[5].label}</h3>
              <p className="text-sm text-[#475569] leading-relaxed mb-3">{coreFeatures[5].desc}</p>
              <div className="flex flex-wrap gap-1.5">
                {coreFeatures[5].pills.map((p) => (
                  <span key={p} className="text-[10px] font-semibold px-2 py-0.5 rounded-full text-[#7C3AED]" style={{ background: 'rgba(124,58,237,0.07)' }}>{p}</span>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── 3 ORDER CHANNELS ── */}
      <div className="py-24" style={{ background: '#FAF5FF' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(124,58,237,0.3)] bg-[rgba(124,58,237,0.07)] text-[11px] font-semibold tracking-widest text-[#7C3AED] uppercase mb-4">
              Order Channels
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Customers order the way{' '}
              <span className="italic font-light text-[#7C3AED]">they prefer.</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-xl mx-auto">Your store is accessible through three channels — every order lands in the same dashboard regardless of how it was placed.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {orderChannels.map((c, i) => (
              <div
                key={c.label}
                className={`animate-on-scroll stagger-${i + 2} group rounded-2xl overflow-hidden border hover:shadow-lg transition-all duration-300`}
                style={{ borderColor: c.color + '25' }}
              >
                <div className="p-7" style={{ background: c.bg }}>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform" style={{ background: c.color }}>
                      <Icon name={c.icon as Parameters<typeof Icon>[0]['name']} size={26} className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-semibold text-[#0F172A]">{c.label}</h3>
                      <p className="text-xs text-[#64748B]">{c.sub}</p>
                    </div>
                  </div>
                  <p className="text-sm text-[#475569] leading-relaxed">{c.desc}</p>
                </div>
                <div className="px-7 py-4 bg-white border-t flex items-center gap-2" style={{ borderColor: c.color + '15' }}>
                  <div className="w-2 h-2 rounded-full" style={{ background: c.color }} />
                  <span className="text-xs font-semibold text-[#64748B]">All orders sync to one dashboard</span>
                </div>
              </div>
            ))}
          </div>

          <div className="animate-on-scroll stagger-4 rounded-2xl p-7 bg-white border border-[rgba(124,58,237,0.15)] flex flex-col sm:flex-row items-center gap-6">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #7C3AED, #6D28D9)' }}>
              <Icon name="AdjustmentsHorizontalIcon" size={24} className="text-white" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <p className="font-semibold text-[#0F172A] mb-1">One unified order dashboard for all three channels</p>
              <p className="text-sm text-[#475569]">Whether a customer orders via your app, WhatsApp, or web link — every order appears in your store dashboard in real time, ready to accept and dispatch.</p>
            </div>
            <Link href="/demo-booking" className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold text-white" style={{ background: 'linear-gradient(135deg, #7C3AED, #6D28D9)' }}>
              See it live
              <Icon name="ArrowRightIcon" size={15} />
            </Link>
          </div>
        </div>
      </div>

      {/* ── 3 APPS ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(124,58,237,0.3)] bg-[rgba(124,58,237,0.07)] text-[11px] font-semibold tracking-widest text-[#7C3AED] uppercase mb-4">
              Platform Apps
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Customer, Store & Rider —{' '}
              <span className="italic font-light text-[#7C3AED]">every role covered.</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Customer App */}
            <div className="animate-on-scroll stagger-2 rounded-2xl overflow-hidden border border-[rgba(124,58,237,0.15)] shadow-sm hover:shadow-md transition-shadow">
              <div className="px-7 py-6" style={{ background: 'linear-gradient(135deg, #4C1D95, #7C3AED)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center">
                    <Icon name="DevicePhoneMobileIcon" size={22} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-white">Customer App</h3>
                    <p className="text-white/60 text-xs">iOS & Android — Your Brand</p>
                  </div>
                </div>
              </div>
              <div className="p-5 grid sm:grid-cols-2 gap-2 bg-[#FAF5FF]">
                {customerAppFeatures.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 p-2.5 rounded-xl bg-white border border-[rgba(124,58,237,0.07)]">
                    <div className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: 'rgba(124,58,237,0.12)' }}>
                      <Icon name="CheckIcon" size={9} className="text-[#7C3AED]" />
                    </div>
                    <span className="text-xs font-medium text-[#0F172A]">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Store Dashboard */}
            <div className="animate-on-scroll stagger-3 rounded-2xl overflow-hidden border border-[rgba(15,23,42,0.07)] shadow-sm hover:shadow-md transition-shadow">
              <div className="px-7 py-6 bg-[#0F172A]">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center">
                    <Icon name="ComputerDesktopIcon" size={22} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-white">Store Dashboard</h3>
                    <p className="text-white/50 text-xs">Web — For Store Owners</p>
                  </div>
                </div>
              </div>
              <div className="p-5 grid sm:grid-cols-2 gap-2 bg-[#F5F5F5]">
                {storeFeatures.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 p-2.5 rounded-xl bg-white border border-[rgba(15,23,42,0.06)]">
                    <div className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center bg-[#0F172A]">
                      <Icon name="CheckIcon" size={9} className="text-white" />
                    </div>
                    <span className="text-xs font-medium text-[#0F172A]">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Rider App */}
            <div className="animate-on-scroll stagger-4 rounded-2xl overflow-hidden border border-[rgba(124,58,237,0.15)] shadow-sm hover:shadow-md transition-shadow">
              <div className="px-7 py-6" style={{ background: 'linear-gradient(135deg, #6D28D9, #7C3AED)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center">
                    <Icon name="TruckIcon" size={22} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-white">Rider App</h3>
                    <p className="text-white/60 text-xs">iOS & Android — Your Fleet</p>
                  </div>
                </div>
              </div>
              <div className="p-5 flex flex-col gap-2 bg-[#FAF5FF]">
                {riderFeatures.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 p-3 rounded-xl bg-white border border-[rgba(124,58,237,0.07)]">
                    <div className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: 'rgba(124,58,237,0.12)' }}>
                      <Icon name="CheckIcon" size={9} className="text-[#7C3AED]" />
                    </div>
                    <span className="text-sm font-medium text-[#0F172A]">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── STORE PERFORMANCE DASHBOARD ── */}
      <div className="py-24" style={{ background: '#FAF5FF' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-on-scroll stagger-1">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(124,58,237,0.3)] bg-[rgba(124,58,237,0.07)] text-[11px] font-semibold tracking-widest text-[#7C3AED] uppercase mb-5">
                Performance Dashboard
              </span>
              <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight mb-5">
                Know exactly how{' '}
                <span className="italic font-light text-[#7C3AED]">your store is performing.</span>
              </h2>
              <p className="text-[#475569] text-lg leading-relaxed mb-8">
                A single dashboard shows you everything — daily sales, which products are flying and which are stuck, peak order hours, and how many customers are coming back. Built for store owners who want insight without complexity.
              </p>
              <div className="flex flex-col gap-3">
                {[
                  'Revenue trend — daily, weekly, monthly view',
                  'Order volume and average order value',
                  'Top-selling products ranked by revenue',
                  'Peak hour analysis for staffing decisions',
                  'Customer repeat rate and retention tracking',
                  'Delivery time and rider performance metrics',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #7C3AED, #6D28D9)' }}>
                      <Icon name="CheckIcon" size={11} className="text-white" />
                    </div>
                    <span className="text-sm text-[#475569]">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="animate-on-scroll stagger-2 grid grid-cols-2 gap-4">
              {dashboardMetrics.map((m, i) => (
                <div
                  key={m.label}
                  className={`animate-on-scroll stagger-${i + 2} rounded-2xl p-5 bg-white border border-[rgba(124,58,237,0.1)] hover:border-[rgba(124,58,237,0.3)] hover:shadow-md transition-all group`}
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" style={{ background: 'rgba(124,58,237,0.1)' }}>
                    <Icon name={m.icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-[#7C3AED]" />
                  </div>
                  <p className="font-semibold text-sm text-[#0F172A] mb-1">{m.label}</p>
                  <p className="text-xs text-[#64748B] leading-relaxed">{m.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── ADDITIONAL FEATURES ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-12">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(124,58,237,0.3)] bg-[rgba(124,58,237,0.07)] text-[11px] font-semibold tracking-widest text-[#7C3AED] uppercase mb-4">
              More Capabilities
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Everything else to run{' '}
              <span className="italic font-light text-[#7C3AED]">a smooth store.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
            {additionalFeatures.map((f, i) => (
              <div
                key={f.label}
                className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} flex items-center gap-4 p-6 rounded-2xl border border-[rgba(124,58,237,0.1)] bg-[#FAF5FF] hover:border-[rgba(124,58,237,0.3)] hover:bg-white hover:shadow-md transition-all duration-300 group`}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform" style={{ background: 'rgba(124,58,237,0.1)' }}>
                  <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-[#7C3AED]" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-[#0F172A]">{f.label}</p>
                  <p className="text-xs text-[#64748B] mt-0.5">{f.sub}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="animate-on-scroll stagger-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            {['Your Own Brand', 'No Marketplace Fees', 'Own Delivery Fleet', 'White-label Ready'].map((item) => (
              <div key={item} className="flex items-center gap-2 p-3 bg-[#FAF5FF] rounded-xl border border-[rgba(124,58,237,0.1)]">
                <Icon name="CheckBadgeIcon" size={16} className="text-[#7C3AED] flex-shrink-0" />
                <span className="text-xs font-medium text-[#475569]">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="py-24" style={{ background: '#FAF5FF' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className="animate-on-scroll stagger-1 rounded-3xl p-12 md:p-16 relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #1E1B4B 0%, #4C1D95 40%, #7C3AED 75%, #8B5CF6 100%)' }}
          >
            <div className="absolute inset-0 grid-lines opacity-15 pointer-events-none" />
            <div className="absolute top-0 right-0 w-72 h-72 rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(196,181,253,0.2) 0%, transparent 70%)', transform: 'translate(50px,-50px)' }} />

            <span className="relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#C4B5FD]/30 bg-[#C4B5FD]/10 text-[11px] font-semibold tracking-widest text-[#C4B5FD] uppercase mb-6">
              <span className="relative flex h-1.5 w-1.5">
                <span className="ping-slow absolute inline-flex h-full w-full rounded-full bg-[#C4B5FD] opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#C4B5FD]" />
              </span>
              Ready to Launch?
            </span>

            <h2 className="relative font-display text-4xl md:text-5xl font-semibold text-white tracking-tight mb-4">
              Your branded store,{' '}
              <span className="italic font-light text-[#C4B5FD]">live in 21 days.</span>
            </h2>

            <p className="relative text-white/60 text-lg mb-5 max-w-xl mx-auto leading-relaxed">
              For demo or more information
            </p>

            <div className="relative flex flex-wrap justify-center gap-x-5 gap-y-2 mb-10 text-sm">
              <a href="tel:+919600830488" className="inline-flex items-center gap-2 text-white/65 font-medium hover:text-white transition-colors">
                <Icon name="PhoneIcon" size={14} />
                +91 9600830488
              </a>
              <span className="text-white/25 hidden sm:inline">|</span>
              <a href="mailto:contact@movio.in" className="inline-flex items-center gap-2 text-white/65 font-medium hover:text-white transition-colors">
                <Icon name="EnvelopeIcon" size={14} />
                contact@movio.in
              </a>
              <span className="text-white/25 hidden sm:inline">|</span>
              <span className="inline-flex items-center gap-2 text-white/65 font-medium">
                <Icon name="GlobeAltIcon" size={14} />
                www.movio.in
              </span>
            </div>

            <div className="relative flex flex-wrap justify-center gap-4">
              <Link
                href="/demo-booking"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-base font-semibold text-[#1E1B4B] hover:opacity-90 shadow-lg transition-all hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg, #C4B5FD, #A78BFA)' }}
              >
                Book Free Demo
                <Icon name="ArrowRightIcon" size={18} />
              </Link>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-base font-medium text-white border-2 border-white/30 hover:border-white/60 hover:bg-white/10 transition-all duration-200"
              >
                <Icon name="PhoneIcon" size={16} />
                Talk to Sales
              </Link>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
